// src/posts/Zero.jsx
// Scaling Training · Part 2 of 3 — ZeRO: Sharding the Optimizer.
// TAGS FOR REGISTRATION: ['training', 'memory', 'distributed']
// EXCERPT: Data-parallel training stores the whole model — params, grads, optimizer — on every GPU. ZeRO shards the redundant copies one bucket at a time. Three stages, three memory wins, almost no extra communication until the last step.
import React, { useEffect, useRef, useState } from 'react';
import usePageMeta from '../usePageMeta';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './PostChrome.css';
import './Zero.css';

/* ============================================================
   KaTeX wrapper
   ============================================================ */
function Katex({ tex, block = false, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current) return;
      if (window.katex) {
        try {
          window.katex.render(tex, ref.current, { displayMode: block, throwOnError: false });
        } catch {
          if (ref.current) ref.current.textContent = tex;
        }
      } else {
        setTimeout(render, 60);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [tex, block]);
  return block
    ? <div ref={ref} className={`zr-katex-block ${className}`} />
    : <span ref={ref} className={`zr-katex-inline ${className}`} />;
}

/* ============================================================
   Widget 1 — Memory stripe per GPU
   Show 4 GPUs as columns, stacked 16-byte budget per param.
   Toggle stages: baseline → ZeRO-1 → ZeRO-2 → ZeRO-3.
   Replicated buckets are full-height per GPU; sharded buckets
   shrink to 1/N height (and shift visual style to "shared").
   ============================================================ */
const STAGES = [
  {
    id: 'baseline',
    name: 'Baseline DDP',
    short: 'each GPU keeps everything',
    sharded: new Set(),
  },
  {
    id: 'zero1',
    name: 'ZeRO-1',
    short: 'shard optimizer state',
    sharded: new Set(['master', 'mom', 'var']),
  },
  {
    id: 'zero2',
    name: 'ZeRO-2',
    short: 'shard optimizer + grads',
    sharded: new Set(['master', 'mom', 'var', 'grads']),
  },
  {
    id: 'zero3',
    name: 'ZeRO-3',
    short: 'shard everything',
    sharded: new Set(['master', 'mom', 'var', 'grads', 'params']),
  },
];

const BUCKETS = [
  { id: 'params',  label: 'fp16 params',    bytes: 2, color: 'var(--zr-params)' },
  { id: 'grads',   label: 'fp16 grads',     bytes: 2, color: 'var(--zr-grads)' },
  { id: 'master',  label: 'fp32 master',    bytes: 4, color: 'var(--zr-master)' },
  { id: 'mom',     label: 'fp32 momentum',  bytes: 4, color: 'var(--zr-mom)' },
  { id: 'var',     label: 'fp32 variance',  bytes: 4, color: 'var(--zr-var)' },
];

function MemoryStripe() {
  const [stageIdx, setStageIdx] = useState(0);
  const stage = STAGES[stageIdx];
  const N = 4; // number of GPUs shown

  // Per-GPU memory in bytes / param: full-height for replicated + 1/N for sharded.
  const perGpuBytes = BUCKETS.reduce((acc, b) => acc + (stage.sharded.has(b.id) ? b.bytes / N : b.bytes), 0);
  const baseline = BUCKETS.reduce((acc, b) => acc + b.bytes, 0); // 16

  const W = 760, H = 360;
  const padL = 56, padR = 18, padT = 28, padB = 56;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const colGap = 14;
  const colW = (innerW - colGap * (N - 1)) / N;
  const bytePx = innerH / baseline;

  return (
    <div className="viz-panel zr-stripe">
      <div className="zr-stage-tabs">
        {STAGES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setStageIdx(i)}
            className={`zr-stage-btn ${stageIdx === i ? 'is-active' : ''}`}
          >
            <span className="zr-stage-name">{s.name}</span>
            <span className="zr-stage-sub">{s.short}</span>
          </button>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="zr-chart" preserveAspectRatio="xMidYMid meet">
        {/* y-axis ticks: 0, 4, 8, 12, 16 bytes */}
        {[0, 4, 8, 12, 16].map((v) => (
          <g key={v}>
            <line x1={padL - 4} y1={padT + innerH - v * bytePx} x2={padL + innerW} y2={padT + innerH - v * bytePx}
                  stroke="var(--post-border)" strokeOpacity="0.5" strokeDasharray="2 4" />
            <text x={padL - 8} y={padT + innerH - v * bytePx + 4} textAnchor="end" fontSize="11"
                  fill="var(--post-text-mut)" fontFamily="var(--post-mono)">{v}</text>
          </g>
        ))}
        <text x={14} y={padT + innerH / 2} textAnchor="middle" fontSize="12"
              fill="var(--post-text-mid)"
              transform={`rotate(-90 14 ${padT + innerH / 2})`}>bytes / parameter</text>

        {/* GPU columns */}
        {Array.from({ length: N }).map((_, gi) => {
          const x = padL + gi * (colW + colGap);
          let y = padT + innerH;
          return (
            <g key={gi}>
              {BUCKETS.map((b) => {
                const isSharded = stage.sharded.has(b.id);
                const fullH = b.bytes * bytePx;
                const ownedH = isSharded ? fullH / N : fullH;
                y -= ownedH;
                return (
                  <g key={b.id}>
                    <rect
                      x={x} y={y} width={colW} height={ownedH}
                      fill={b.color}
                      className={isSharded ? 'zr-bucket sharded' : 'zr-bucket replicated'}
                    />
                    {/* ghosted phantom showing what was sharded away */}
                    {isSharded && (
                      <rect
                        x={x} y={y - (fullH - ownedH)} width={colW} height={fullH - ownedH}
                        fill={b.color} fillOpacity="0.12"
                        stroke={b.color} strokeOpacity="0.35" strokeDasharray="3 3"
                      />
                    )}
                  </g>
                );
              })}
              <text x={x + colW / 2} y={padT + innerH + 18} textAnchor="middle"
                    fontSize="11" fontFamily="var(--post-mono)" fill="var(--post-text-mid)">
                GPU {gi}
              </text>
            </g>
          );
        })}

        {/* legend */}
        <g transform={`translate(${padL}, ${H - 18})`}>
          {BUCKETS.map((b, i) => (
            <g key={b.id} transform={`translate(${i * 130}, 0)`}>
              <rect x={0} y={-9} width={12} height={12} rx={2} fill={b.color} />
              <text x={18} y={1} fontSize="11" fill="var(--post-text-mid)">{b.label}</text>
              <text x={18} y={13} fontSize="10" fontFamily="var(--post-mono)" fill="var(--post-text-mut)">
                {b.bytes} B/param
              </text>
            </g>
          ))}
        </g>
      </svg>

      <div className="zr-kpis">
        <div className="zr-kpi">
          <div className="zr-kpi-label">Memory / GPU (bytes/param)</div>
          <div className="zr-kpi-val">{perGpuBytes.toFixed(2)}</div>
          <div className="zr-kpi-sub">
            baseline {baseline} · <strong>{(baseline / perGpuBytes).toFixed(1)}×</strong> reduction
          </div>
        </div>
        <div className="zr-kpi">
          <div className="zr-kpi-label">Sharded across</div>
          <div className="zr-kpi-val">N = {N}</div>
          <div className="zr-kpi-sub">data-parallel workers in this view</div>
        </div>
      </div>
      <p className="zr-caption">
        Solid blocks are <em>owned</em> on this GPU; dashed ghosts above each
        bar show the same bucket living on the other three GPUs. As you walk
        the stages, more buckets convert from replicated (full-height every
        column) to sharded (1/N-height per column).
      </p>
    </div>
  );
}

/* ============================================================
   Widget 2 — Communication per training step
   Stage tabs (reuse). For each stage, show the comm ops as a
   row of labeled blocks with a total-volume counter.
   ============================================================ */
const COMM_OPS = {
  baseline: [
    { op: 'all-reduce', volume: 2, what: 'gradients (≡ reduce-scatter Ψ + all-gather Ψ)' },
  ],
  zero1: [
    { op: 'reduce-scatter', volume: 1, what: 'gradients to owners' },
    { op: 'all-gather', volume: 1, what: 'updated params back to everyone' },
  ],
  zero2: [
    { op: 'reduce-scatter', volume: 1, what: 'gradients to owners' },
    { op: 'all-gather', volume: 1, what: 'updated params back to everyone' },
  ],
  zero3: [
    { op: 'all-gather (fwd)', volume: 1, what: 'params layer-by-layer for forward' },
    { op: 'all-gather (bwd)', volume: 1, what: 'params layer-by-layer for backward' },
    { op: 'reduce-scatter', volume: 1, what: 'gradients to owners' },
  ],
};

/* ============================================================
   Widget — Collectives visualizer
   Three tabs (all-reduce / reduce-scatter / all-gather), each
   showing BEFORE (top row of 4 GPUs) → AFTER (bottom row).
   Each GPU is a rectangle containing 4 chunks. Color encodes
   "who owned this data originally"; Σ marks reduced cells;
   blank marks discarded cells.
   ============================================================ */
const GPU_COLORS = [
  'var(--zr-params)',  // blue
  'var(--zr-grads)',   // orange
  'var(--zr-master)',  // purple
  'var(--zr-mom)',     // green
];
const GPU_LABELS = ['A', 'B', 'C', 'D'];

const COLLECTIVES = [
  {
    id: 'all-reduce',
    name: 'all-reduce',
    volume: '2Ψ',
    blurb: 'Every GPU starts with its own copy of the same-shape tensor. After the op, every GPU holds the elementwise sum.',
    before: (gpu) => Array.from({ length: 4 }, () => ({ owner: gpu, kind: 'own' })),
    after:  ()    => Array.from({ length: 4 }, () => ({ kind: 'reduced' })),
  },
  {
    id: 'reduce-scatter',
    name: 'reduce-scatter',
    volume: 'Ψ',
    blurb: 'Each GPU keeps only its assigned slice of the reduced result. Diagonal lands here, the rest is discarded.',
    before: (gpu) => Array.from({ length: 4 }, () => ({ owner: gpu, kind: 'own' })),
    after:  (gpu) => Array.from({ length: 4 }, (_, c) => c === gpu ? { kind: 'reduced' } : { kind: 'empty' }),
  },
  {
    id: 'all-gather',
    name: 'all-gather',
    volume: 'Ψ',
    blurb: 'Each GPU starts holding only its own slice. After the op, every GPU has the full tensor stitched together.',
    before: (gpu) => Array.from({ length: 4 }, (_, c) => c === gpu ? { owner: gpu, kind: 'own' } : { kind: 'empty' }),
    after:  ()    => Array.from({ length: 4 }, (_, c) => ({ owner: c, kind: 'own' })),
  },
];

function Collectives() {
  const [opIdx, setOpIdx] = useState(0);
  const op = COLLECTIVES[opIdx];
  const N = 4;

  const W = 760, H = 320;
  const padL = 56, padR = 18, padT = 36, padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const colGap = 18;
  const gpuW = (innerW - colGap * (N - 1)) / N;
  const rowGap = 70;
  const gpuH = (innerH - rowGap) / 2;
  const cellGap = 3;
  const cellW = (gpuW - cellGap * 5) / 4;
  const cellH = gpuH - 8;

  const renderGpu = (cells, x, y) => (
    <g>
      <rect x={x} y={y} width={gpuW} height={gpuH} rx={6}
            fill="white" stroke="var(--post-border)" />
      {cells.map((cell, ci) => {
        const cx = x + cellGap + ci * (cellW + cellGap);
        const cy = y + 4;
        let fill = '#f1f5f9';
        let stroke = 'var(--post-border)';
        let label = '';
        let labelFill = 'var(--post-text-mut)';
        if (cell.kind === 'own') {
          fill = GPU_COLORS[cell.owner];
          stroke = GPU_COLORS[cell.owner];
          label = GPU_LABELS[cell.owner];
          labelFill = '#fff';
        } else if (cell.kind === 'reduced') {
          fill = '#1e293b';
          stroke = '#1e293b';
          label = 'Σ';
          labelFill = '#fff';
        } else {
          label = '·';
        }
        return (
          <g key={ci}>
            <rect x={cx} y={cy} width={cellW} height={cellH} rx={3}
                  fill={fill} stroke={stroke} />
            <text x={cx + cellW / 2} y={cy + cellH / 2 + 4}
                  textAnchor="middle" fontSize="13"
                  fontFamily="var(--post-mono)" fontWeight="600"
                  fill={labelFill}>{label}</text>
          </g>
        );
      })}
    </g>
  );

  return (
    <div className="viz-panel zr-coll">
      <div className="zr-stage-tabs zr-coll-tabs">
        {COLLECTIVES.map((c, i) => (
          <button
            key={c.id}
            onClick={() => setOpIdx(i)}
            className={`zr-stage-btn ${opIdx === i ? 'is-active' : ''}`}
          >
            <span className="zr-stage-name">{c.name}</span>
            <span className="zr-stage-sub">{c.volume} on the wire</span>
          </button>
        ))}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="zr-chart" preserveAspectRatio="xMidYMid meet">
        {/* row labels */}
        <text x={padL - 10} y={padT + gpuH / 2 + 4} textAnchor="end" fontSize="11"
              fontFamily="var(--post-mono)" fill="var(--post-text-mut)">before</text>
        <text x={padL - 10} y={padT + gpuH + rowGap + gpuH / 2 + 4} textAnchor="end"
              fontSize="11" fontFamily="var(--post-mono)" fill="var(--post-text-mut)">after</text>

        {/* GPU rows */}
        {Array.from({ length: N }).map((_, gi) => {
          const x = padL + gi * (gpuW + colGap);
          const yBefore = padT;
          const yAfter = padT + gpuH + rowGap;
          return (
            <g key={gi}>
              {renderGpu(op.before(gi), x, yBefore)}
              {renderGpu(op.after(gi),  x, yAfter)}
              {/* GPU label between rows */}
              <text x={x + gpuW / 2} y={yBefore + gpuH + rowGap / 2 + 4}
                    textAnchor="middle" fontSize="11"
                    fontFamily="var(--post-mono)" fill="var(--post-text-mid)">
                GPU {gi}
              </text>
              {/* downward arrow */}
              <line x1={x + gpuW / 2} y1={yBefore + gpuH + 8}
                    x2={x + gpuW / 2} y2={yBefore + gpuH + rowGap / 2 - 14}
                    stroke="var(--post-border)" strokeWidth="1.5" />
              <line x1={x + gpuW / 2} y1={yBefore + gpuH + rowGap / 2 + 14}
                    x2={x + gpuW / 2} y2={yAfter - 8}
                    stroke="var(--post-border)" strokeWidth="1.5" />
            </g>
          );
        })}
      </svg>

      <p className="zr-caption">
        {op.blurb}{' '}
        <span className="zr-mut">
          Σ = reduced (summed) cell · letter = owner's original data · · = empty (no longer held).
        </span>
      </p>
    </div>
  );
}

function CommSteps() {
  const [stageIdx, setStageIdx] = useState(0);
  const stage = STAGES[stageIdx];
  const ops = COMM_OPS[stage.id];
  const total = ops.reduce((acc, o) => acc + o.volume, 0);

  return (
    <div className="viz-panel zr-comm">
      <div className="zr-stage-tabs">
        {STAGES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setStageIdx(i)}
            className={`zr-stage-btn ${stageIdx === i ? 'is-active' : ''}`}
          >
            <span className="zr-stage-name">{s.name}</span>
            <span className="zr-stage-sub">{s.short}</span>
          </button>
        ))}
      </div>

      <div className="zr-ops">
        {ops.map((o, i) => (
          <div key={i} className="zr-op">
            <div className="zr-op-name">{o.op}</div>
            <div className="zr-op-vol">
              <Katex tex="\Psi" /> bytes
            </div>
            <div className="zr-op-what">{o.what}</div>
          </div>
        ))}
      </div>

      <div className="zr-kpis">
        <div className="zr-kpi">
          <div className="zr-kpi-label">Total comm volume / step</div>
          <div className="zr-kpi-val">{total} <span className="zr-kpi-tail">Ψ</span></div>
          <div className="zr-kpi-sub">
            baseline 2Ψ · <strong>{total === 2 ? '1×' : `${(total / 2).toFixed(1)}×`}</strong>{' '}
            DDP
          </div>
        </div>
        <div className="zr-kpi">
          <div className="zr-kpi-label">Extra over DDP</div>
          <div className="zr-kpi-val">{total === 2 ? '0%' : `+${((total - 2) / 2 * 100).toFixed(0)}%`}</div>
          <div className="zr-kpi-sub">{stage.id === 'zero3' ? 'paid for an N× memory cut' : 'free'}</div>
        </div>
      </div>
      <p className="zr-caption">
        ZeRO-1 and ZeRO-2 replace DDP's single all-reduce with a
        reduce-scatter + all-gather pair — same total bytes on the wire.
        ZeRO-3 adds one extra all-gather (the params have to be reassembled
        once for forward and once for backward), pushing the bill to 3Ψ —
        a 1.5× cost for an N× memory reduction.
      </p>
    </div>
  );
}

/* ============================================================
   Code block — toy DeepSpeed / FSDP configuration sketch
   ============================================================ */
const CODE = `# DeepSpeed config — pick a ZeRO stage.
{
  "train_micro_batch_size_per_gpu": 8,
  "zero_optimization": {
    # 0 = vanilla DDP (no sharding)
    # 1 = shard optimizer state
    # 2 = shard optimizer state + gradients
    # 3 = shard everything (params, grads, optimizer)
    "stage": 3,

    # Stage-3-only knobs:
    # how many bytes to all-gather at once (smaller = less peak memory,
    # more comm calls). Default ~5e8 is a fine starting point.
    "stage3_max_live_parameters": 1e9,
    "stage3_prefetch_bucket_size":  5e8,
  }
}

# Or in PyTorch directly via FSDP (same idea, integrated into core):
from torch.distributed.fsdp import FullyShardedDataParallel as FSDP
from torch.distributed.fsdp import ShardingStrategy

# FULL_SHARD          ≈ ZeRO-3
# SHARD_GRAD_OP       ≈ ZeRO-2
# NO_SHARD            = vanilla DDP
model = FSDP(model, sharding_strategy=ShardingStrategy.FULL_SHARD)
`;

function CodeBlock() {
  return (
    <details className="post-code-details zr-code">
      <summary>
        <span className="post-code-summary-icon">›</span>
        <span>DeepSpeed / FSDP — picking a stage</span>
        <span className="post-code-summary-hint">click to expand</span>
      </summary>
      <SyntaxHighlighter
        language="python"
        style={oneLight}
        customStyle={{ background: 'transparent', margin: 0, padding: 0, fontSize: '13.5px' }}
        codeTagProps={{ style: { background: 'transparent', textShadow: 'none' } }}
      >
        {CODE}
      </SyntaxHighlighter>
    </details>
  );
}

/* ============================================================
   Page
   ============================================================ */
export default function Zero() {
  usePageMeta({
    title: 'ZeRO: Sharding the Optimizer',
    description: 'Vanilla data parallelism replicates the entire model — params, grads, optimizer state — on every GPU. ZeRO shards each bucket across the data-parallel workers. Three stages, three memory wins, almost no extra communication.',
    slug: 'zero',
    publishedDate: '2026-06-11',
    keywords: ['ZeRO', 'DeepSpeed', 'sharding', 'data parallel', 'training', 'memory'],
  });

  return (
    <article className="post-2026 zr-post">
      <div className="post-wrap">
        <div className="post-hero">
          <div className="post-series-tag">
            <span className="post-live-dot" aria-hidden="true" />Scaling Training · Part 2 of 5
          </div>
          <h1>ZeRO: Sharding the Optimizer</h1>
          <p className="post-lede">
            A 7.5-billion-parameter model needs <strong>120 GB</strong> on
            every GPU under vanilla data parallelism — far more than any
            single device has. The math says it should fit on a handful of
            GPUs combined, though. ZeRO is the trick that gets you from
            "every GPU has a full copy" to "every GPU has its share."
          </p>
          <div className="post-byline">
            By <strong>Bernhard Walser</strong> &amp;{' '}
            <a className="post-link" href="https://www.anthropic.com/claude" target="_blank" rel="noreferrer"><strong>Claude</strong></a>
            {' '}(Anthropic) · co-written and co-designed ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>

        <h2 className="reveal">Why DDP wastes memory</h2>
        <p>
          The standard recipe — Distributed Data Parallel — gives every GPU
          a full copy of the model. Each worker reads its own slice of the
          batch, computes its own gradients, all-reduces them, and applies
          the same optimizer step locally. Simple, and the math works out:
          all GPUs end up with the same updated weights.
        </p>
        <p>
          It's also enormously wasteful. With <Katex tex="N" /> workers, you
          have <Katex tex="N" /> identical copies of the parameters,{' '}
          <Katex tex="N" /> identical copies of the gradients, and{' '}
          <Katex tex="N" /> identical copies of the optimizer state. The
          replication buys you nothing — the model never differs across
          GPUs — and it caps how big a model you can train at whatever
          fits on a single device.
        </p>

        <h2 className="reveal">The 16-byte budget</h2>
        <p>
          To see how bad it is, count bytes per parameter for{' '}
          <strong>mixed-precision Adam</strong>, the standard training
          recipe:
        </p>
        <ul className="zr-budget">
          <li>
            <span className="zr-bullet zr-c-params" />
            <strong>fp16 parameters</strong> — what runs in forward and
            backward: <code>2 B/param</code>
          </li>
          <li>
            <span className="zr-bullet zr-c-grads" />
            <strong>fp16 gradients</strong> — accumulated during backward:
            <code> 2 B/param</code>
          </li>
          <li>
            <span className="zr-bullet zr-c-master" />
            <strong>fp32 master parameters</strong> — the precise copy
            optimizer steps land on: <code>4 B/param</code>
          </li>
          <li>
            <span className="zr-bullet zr-c-mom" />
            <strong>fp32 momentum</strong> — Adam's first moment:
            <code> 4 B/param</code>
          </li>
          <li>
            <span className="zr-bullet zr-c-var" />
            <strong>fp32 variance</strong> — Adam's second moment:
            <code> 4 B/param</code>
          </li>
        </ul>
        <p>
          Total: <strong>16 bytes per parameter</strong>. For a 7.5B model
          that's <strong>120 GB</strong> per GPU just for "model state" —
          before activations, before temporary buffers, before anything
          else. No 32 GB or 40 GB GPU is touching it.
        </p>
        <p>
          But notice: the optimizer state alone (master + momentum +
          variance) is <strong>12 of the 16 bytes</strong>. And it's used
          only during the optimizer step — once per training iteration,
          right at the end. If each GPU only owned its slice of those 12
          bytes, it could shed three-quarters of the bill almost for free.
          That's ZeRO-1.
        </p>

        <h2 className="reveal">Three stages, three buckets</h2>
        <p>
          ZeRO partitions the buckets across data-parallel workers, one
          stage at a time. Each stage strips out more redundancy:
        </p>
        <ul className="zr-stages-list">
          <li>
            <strong>ZeRO-1</strong> shards the <em>optimizer state</em>{' '}
            (12 bytes). Memory per GPU drops from{' '}
            <Katex tex="16" /> to <Katex tex="4 + 12/N" />.
          </li>
          <li>
            <strong>ZeRO-2</strong> additionally shards the{' '}
            <em>gradients</em> (2 more bytes).{' '}
            <Katex tex="2 + 14/N" /> per GPU.
          </li>
          <li>
            <strong>ZeRO-3</strong> shards the <em>parameters</em> too
            (the last 2 bytes). <Katex tex="16/N" /> per GPU.
          </li>
        </ul>
        <p>
          For <Katex tex="N = 4" /> data-parallel workers the bars look
          like this:
        </p>
        <MemoryStripe />
        <p>
          At <Katex tex="N = 64" />, ZeRO-3 cuts the 120 GB down to under{' '}
          <strong>2 GB</strong> per GPU. That's the paper's headline
          number — the same model state that doesn't fit on any single
          device fits comfortably when 64 of them split it.
        </p>

        <h2 className="reveal">Three collectives, one diagram</h2>
        <p>
          The sharding only pays off if the communication doesn't get out
          of hand. Before walking the per-stage cost, here are the three
          collective operations ZeRO leans on — switch tabs to see what
          each one does to a tensor split four ways across four GPUs:
        </p>
        <Collectives />
        <p>
          The key identity:{' '}
          <strong>all-reduce ≡ reduce-scatter + all-gather</strong>. A
          single all-reduce moves <Katex tex="2\Psi" /> bytes on the wire
          (every cell visits every GPU once); split into its two halves
          you get <Katex tex="\Psi" /> for each, same total. ZeRO-1 and
          ZeRO-2 use exactly that split — they reduce-scatter the
          gradients so each GPU only keeps its slice's sum, do the
          optimizer step locally, then all-gather the updated parameters
          back out.
        </p>

        <h2 className="reveal">What the training step actually does</h2>
        <p>
          With the collectives nailed down, walk through what one training
          step looks like at each stage:
        </p>
        <p>
          <strong>Baseline DDP.</strong> One all-reduce on the gradients,{' '}
          <Katex tex="2\Psi" /> on the wire. Everyone updates locally.
        </p>
        <p>
          <strong>ZeRO-1 and ZeRO-2.</strong> Split the all-reduce into
          reduce-scatter + all-gather. Each GPU only holds its slice of
          the gradients (after the scatter), only updates its slice of
          the parameters, and then the gather propagates the fresh
          parameters back. Same <Katex tex="2\Psi" /> total.
        </p>
        <p>
          <strong>ZeRO-3.</strong> Now nobody owns the full parameter
          tensor any more. Before each layer's forward, an all-gather
          rebuilds that layer's parameters across the cluster; everyone
          runs forward; the non-owners discard their copies. Same dance
          again on backward. Then one reduce-scatter for the gradients at
          the end. Total: <Katex tex="3\Psi" /> — a 1.5× DDP bill for a
          potentially 1000× memory reduction.
        </p>
        <CommSteps />

        <h2 className="reveal">Why the extra comm is tolerable</h2>
        <p>
          50% more bytes on the wire sounds painful, but two things make
          it manageable. First, the all-gather is{' '}
          <strong>pipelined</strong>: while layer <em>k</em>'s parameters
          are being gathered, layer <em>k − 1</em>'s forward is still
          running. The latency hides behind compute. Second, you've
          freed up enough memory on each GPU that you can afford a much
          larger local batch — which improves arithmetic intensity and
          often more than compensates for the comm overhead. The paper
          even reports <em>super-linear</em> speedups in some regimes.
        </p>

        <h2 className="reveal">Where ZeRO sits in the parallelism landscape</h2>
        <p>
          ZeRO is one axis of parallelism. The other two are worth knowing
          because real training runs use all three at once — what people
          call <strong>3D parallelism</strong>.
        </p>
        <ul className="zr-stages-list">
          <li>
            <strong>Data parallel (this post).</strong> Every GPU runs the
            <em> same model</em> on a <em>different slice of the batch</em>.
            Memory savings come from <em>not duplicating</em> the model
            state across the data-parallel workers — that's ZeRO. The
            classical version is plain DDP.
          </li>
          <li>
            <strong>Tensor (model) parallel.</strong> Every GPU runs a
            <em> different slice of the same layer</em> on the
            <em> same data</em>. Each matmul is split:{' '}
            <Katex tex="Y = XW" /> becomes "split <Katex tex="W" /> column-
            or row-wise, each GPU computes its piece, all-reduce the
            partials." The canonical paper is{' '}
            <a className="post-link" href="https://arxiv.org/abs/1909.08053" target="_blank" rel="noreferrer">
              Megatron-LM (Shoeybi et al. 2019)
            </a>{' '}— same year as ZeRO, same lab (NVIDIA), different axis.
            It's the only way to fit a single layer if the layer itself is
            too big for one GPU (e.g. an MLP intermediate dimension of
            ≥25 000).
          </li>
          <li>
            <strong>Pipeline parallel.</strong> Split <em>layers</em> across
            GPUs — GPU 0 owns layers 1–8, GPU 1 owns layers 9–16, etc.
            Micro-batches flow down the pipeline like an assembly line.
            GPipe / PipeDream / 1F1B are the algorithm variants.
          </li>
        </ul>

        <h3 className="reveal">ZeRO vs Megatron — when do you need both?</h3>
        <p>
          The split is mostly about <strong>granularity</strong>. Tensor
          parallel comm happens on{' '}
          <em>activation</em>-sized tensors at every layer, so it only
          stays cheap when the GPUs share fast intra-node interconnect
          (NVLink). It scales cleanly to ~8 GPUs and falls off hard across
          nodes. ZeRO comm happens on{' '}
          <em>parameter/gradient</em>-sized tensors once per step, so it
          scales to hundreds or thousands of GPUs even over InfiniBand.
        </p>
        <p>
          The typical recipe for a 100B+ parameter model is therefore:
          tensor parallel <em>within</em> a node (NVLink eats it), data
          parallel + ZeRO <em>across</em> nodes (InfiniBand handles it),
          and pipeline parallel layered on top when even that's not
          enough. ZeRO doesn't replace Megatron; it picks up where
          Megatron's bandwidth budget runs out.
        </p>

        <h2 className="reveal">In practice</h2>
        <p>
          ZeRO ships as part of <strong>DeepSpeed</strong>. PyTorch later
          adopted the same ideas into core as <strong>FSDP</strong> (Fully
          Sharded Data Parallel) — that's the next post in this series.
          Either way, picking a stage is essentially one knob.
        </p>
        <CodeBlock />
        <p>
          A rough rule of thumb: <strong>ZeRO-1</strong> is free, always
          on. <strong>ZeRO-2</strong> is also free as long as your
          gradient-reduction was already non-trivial.{' '}
          <strong>ZeRO-3</strong> is the heavyweight option — you pay
          1.5× comm and have to be careful about layer granularity, but
          it's the only way to fit anything past ~13B params on a normal
          cluster.
        </p>

        <h2 className="reveal">Caveats and edge cases</h2>
        <p>
          A few things to know. <strong>Communication-bound regimes</strong>{' '}
          (low intra-node bandwidth, small batch sizes) can make ZeRO-3
          slower than tensor-parallel or pipeline-parallel alternatives —
          the comm hiding only works if there's compute to hide behind.
          For very small models ZeRO has no upside and a tiny overhead;
          stick with DDP.
        </p>
        <p>
          ZeRO also composes orthogonally with{' '}
          <a className="post-link" href="/blog/gradient-checkpointing">gradient checkpointing</a> —
          ZeRO shrinks the <em>model state</em> (params, grads,
          optimizer), checkpointing shrinks the <em>activation state</em>.
          Real training runs use both.
        </p>

        <h2 className="reveal">References</h2>
        <div className="post-refs">
          <div className="head ref-cite">Cite</div>
          <div className="head ref-title">Title</div>
          <div className="head ref-link">Link</div>

          <div className="ref-cite">Rajbhandari et al. 2019</div>
          <div>
            <div className="ref-title">
              <a href="https://arxiv.org/abs/1910.02054" target="_blank" rel="noreferrer">ZeRO: Memory Optimizations Toward Training Trillion Parameter Models</a>
            </div>
            <div className="ref-note">The original paper. Introduces P<sub>os</sub>, P<sub>os+g</sub>, P<sub>os+g+p</sub> and the comm analysis.</div>
          </div>
          <div className="ref-link"><a href="https://arxiv.org/abs/1910.02054" target="_blank" rel="noreferrer">arxiv.org/abs/1910.02054</a></div>

          <div className="ref-cite">Shoeybi et al. 2019</div>
          <div>
            <div className="ref-title">
              <a href="https://arxiv.org/abs/1909.08053" target="_blank" rel="noreferrer">Megatron-LM: Training Multi-Billion Parameter Language Models Using Model Parallelism</a>
            </div>
            <div className="ref-note">The tensor-parallel companion to ZeRO — splits each matmul column-/row-wise across NVLink-connected GPUs.</div>
          </div>
          <div className="ref-link"><a href="https://arxiv.org/abs/1909.08053" target="_blank" rel="noreferrer">arxiv.org/abs/1909.08053</a></div>

          <div className="ref-cite">DeepSpeed</div>
          <div>
            <div className="ref-title">
              <a href="https://www.deepspeed.ai/tutorials/zero/" target="_blank" rel="noreferrer">DeepSpeed ZeRO tutorial</a>
            </div>
            <div className="ref-note">Stage selection, config knobs, and the offload variants (ZeRO-Offload, ZeRO-Infinity).</div>
          </div>
          <div className="ref-link"><a href="https://www.deepspeed.ai/tutorials/zero/" target="_blank" rel="noreferrer">deepspeed.ai</a></div>

          <div className="ref-cite">Earlier posts</div>
          <div>
            <div className="ref-title">
              <a href="/blog/gradient-checkpointing">Gradient Checkpointing</a>
            </div>
            <div className="ref-note">The complementary trick — shrinks activation memory, where ZeRO shrinks model-state memory.</div>
          </div>
          <div className="ref-link"><a href="/blog/gradient-checkpointing">/gradient-checkpointing</a></div>
        </div>

        <footer className="post-footer">
          <p>
            <strong>Part 2 of Scaling Training</strong> · Previous:{' '}
            <a className="post-link" href="/blog/gradient-checkpointing">Gradient Checkpointing</a>
            {' · '}Next:{' '}
            <a className="post-link" href="/blog/ring-all-reduce">Ring All-Reduce</a>.
          </p>
          <p style={{ marginBottom: 0 }}>
            Bernhard Walser · ML Engineer, Digitec Galaxus · ETH Computer Science ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </p>
        </footer>
      </div>
    </article>
  );
}
